import { Activity, LogIn, Terminal } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authDevLogin, authGoogleConnectUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/designTokens";
import { toast } from "sonner";

const AUTH_ERROR_MESSAGES = {
  not_configured: "Google Sign-in is not configured yet. Use Dev Login or add OAuth credentials to the server.",
  denied: "Google sign-in was cancelled.",
  state_expired: "Your sign-in session expired. Please try again.",
  token_exchange_failed: "Google sign-in failed. Please try again.",
  userinfo_failed: "Could not load your Google profile. Please try again.",
  email_missing: "Your Google account did not provide an email address.",
  email_unverified: "Your Google email address is not verified.",
};

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [devLoading, setDevLoading] = useState(false);
  const isLoopbackHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const isLocalDev = isLoopbackHost && process.env.REACT_APP_LOCAL_DEV === "true";

  useEffect(() => {
    const authError = searchParams.get("auth_error");
    if (!authError) return;
    toast.error(AUTH_ERROR_MESSAGES[authError] || "Sign in failed. Please try again.");
    searchParams.delete("auth_error");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleLogin = () => {
    window.location.href = authGoogleConnectUrl();
  };

  const handleDevLogin = async () => {
    setDevLoading(true);
    try {
      const user = await authDevLogin();
      setUser(user);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Dev login failed:", err);
      toast.error("Sign in failed. Make sure the backend and database are running.");
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-background text-dark-text-primary grid-bg flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-md border border-brand-primary flex items-center justify-center bg-brand-primary/10">
            <Activity className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading text-3xl leading-none tracking-wider uppercase">{BRAND.productName}</div>
            <div className="text-[9px] tracking-[0.18em] text-dark-text-muted uppercase mt-0.5 max-w-[11rem] leading-tight opacity-80">
              {BRAND.companyName}
            </div>
          </div>
        </div>

        <div className="text-[10px] tracking-[0.4em] uppercase text-dark-text-muted">// Sign In</div>
        <h1 className="font-heading text-5xl sm:text-6xl uppercase tracking-tight mt-2">
          Command Your Data
        </h1>
        <p className="text-sm text-dark-text-secondary mt-4 leading-relaxed">
          Sign in with Google to upload{" "}
          <span className="text-dark-text-primary">.FIT / .GPX / .TCX</span> files, sync directly from{" "}
          <span className="text-[#FC5200]">Strava</span>, and view rich analytics on your{" "}
          <span className="text-dark-text-primary">real recorded</span> performance data.
        </p>

        <button
          onClick={handleLogin}
          className="mt-10 w-full flex items-center justify-center gap-3 bg-brand-primary hover:bg-brand-primary-hover text-white transition-colors px-6 py-4 text-xs tracking-[0.3em] uppercase font-semibold"
          data-testid="google-login-button"
        >
          <LogIn className="w-4 h-4" strokeWidth={2} />
          Sign in with Google
        </button>

        {isLocalDev && (
            <button
            onClick={handleDevLogin}
            disabled={devLoading}
            className="mt-4 w-full flex items-center justify-center gap-3 border border-dark-border hover:border-brand-primary transition-colors px-6 py-4 text-xs tracking-[0.3em] uppercase font-semibold text-dark-text-secondary hover:text-dark-text-primary disabled:opacity-50"
            data-testid="dev-login-button"
          >
            <Terminal className="w-4 h-4" strokeWidth={2} />
            {devLoading ? "Signing in…" : "Dev Login (local)"}
          </button>
        )}

        <div className="mt-8 text-[10px] tracking-[0.25em] uppercase text-dark-text-muted">
          Real data · No estimation · Athlete-grade
        </div>
      </div>
    </div>
  );
};
